import React from 'react';

import { Root, TitleRow, Spark, TitleText, StickySentinel } from './SectionHead.styles';
import { useStuckOnPin } from './useStuckOnPin';

export type SectionHeadProps = {
  title: string;
  /** i18n interpolation values for the title (e.g. the active brand name). */
  placeholders?: Record<string, string | number>;
  /** Optional leading spark/logo asset URL. */
  sparkSrc?: string;
  /** Tighten the top margin for the first header under the topbar. */
  first?: boolean;
  /** Optional right-aligned control (e.g. the interval dropdown). */
  right?: React.ReactNode;
  /** Pin the header under the layout top bar; once pinned it morphs into a
   *  floating pill (solid surface, rounded corners, soft shadow). Opt-in — every
   *  other SectionHead stays a plain in-flow row. */
  sticky?: boolean;
};

/** A dashboard section header: an optional spark glyph + title, with an optional
 *  control pinned to the right. When `sticky`, it pins under the layout top bar
 *  and lifts into a floating pill as the page scrolls beneath it. */
export const SectionHead = ({
  title,
  placeholders,
  sparkSrc,
  first = false,
  right,
  sticky = false,
}: SectionHeadProps): React.ReactElement => {
  const { sentinelRef, stuck } = useStuckOnPin(sticky);

  const head = (
    <Root $first={first} $sticky={sticky} $stuck={stuck} data-stuck={sticky ? stuck : undefined}>
      <TitleRow>
        {sparkSrc ? <Spark src={sparkSrc} alt="" /> : null}
        <TitleText text={title} placeholders={placeholders} tag="span" />
      </TitleRow>
      {right}
    </Root>
  );

  if (!sticky) return head;

  return (
    <React.Fragment>
      <StickySentinel ref={sentinelRef} aria-hidden="true" />
      {head}
    </React.Fragment>
  );
};

export default SectionHead;
